/* eslint-disable @typescript-eslint/consistent-type-assertions */

import * as React from "react";
import { repository } from "clientInstance";
import SimpleExpander from "components/SimpleExpander";
import { WorkerPoolSummaryResource, MachineModelHealthStatus, WorkerPoolResource, DynamicWorkerPoolResource } from "client/resources";
import { OctopusIcon, OctopusIconType } from "components/Icon";
import PaperLayout from "components/PaperLayout/PaperLayout";
import OverflowMenu, { OverflowMenuItems, OverflowMenuNavLink, OverflowMenuGenericItem } from "components/Menu/OverflowMenu";
import { ActionButton, ActionButtonType } from "components/Button";
import { isEqual } from "lodash";
import MachineHealthStatusHelper from "utils/MachineHealthStatusHelper";
import Permission from "client/resources/permission";
const styles = require("./style.less");
import routeLinks from "../../../../../routeLinks";
import BaseMachinesSummary from "../../BaseMachinesSummary/BaseMachinesSummary";
import { SmallCloseButton, BaseMachinesSummaryProps, BaseMachinesSummaryState } from "../../BaseMachinesSummary/BaseMachinesSummary";
import MarkdownDescription from "components/MarkdownDescription";
import { Section } from "components/Section/Section";
import RequestRaceConditioner from "utils/RequestRaceConditioner";
import InternalRedirect from "components/Navigation/InternalRedirect";
import { WorkerPoolType, DynamicWorkerType } from "client/resources/workerPoolsSupportedTypesResouce";
import Callout, { CalloutType } from "components/Callout";
import ExternalLink from "components/Navigation/ExternalLink";
import { withTheme } from "components/Theme";
import { getIsDisabledFilterAsPerPrecedence, getHealthStatusFiltersAsPerPrecedence } from "../../BaseMachinesSummary/BaseMachinesSummaryFilter";
import { orderedHealthStatuses } from "areas/infrastructure/InfrastructureDetails";
import { createWorkerPoolsMachinesArgs, WorkerPoolsSummaryFilter } from "./WorkerPoolsSummaryFilter";
import { MissingChip } from "components/Chips";
import { DeprecatedDynamicWorkerTypeChip } from "../DeprecatedWorkerType";

interface WorkerPoolSummarySectionProps extends BaseMachinesSummaryProps {
    workerPoolSummary: WorkerPoolSummaryResource;
    workerTypes: DynamicWorkerType[];
    filter: WorkerPoolsSummaryFilter;
}

class WorkerPoolSummarySection extends BaseMachinesSummary<WorkerPoolSummarySectionProps, BaseMachinesSummaryState> {
    private requestRaceConditioner = new RequestRaceConditioner();

    constructor(props: WorkerPoolSummarySectionProps) {
        super(props);

        this.state = {
            machinesResponse: null,
            machineHealthStatusFastLookup: {},
            currentPageIndex: 0,
            expanded: false,
            healthStatusFilter: "",
            isDisabledFilter: false,
        };
    }

    componentWillReceiveProps(nextProps: WorkerPoolSummarySectionProps) {
        if (this.state.expanded && !isEqual(this.props.filter, nextProps.filter)) {
            this.reloadDataAndCurrentPageIndex();
        }
    }

    render() {
        if (this.state.redirectToTaskId) {
            return <InternalRedirect to={routeLinks.task(this.state.redirectToTaskId).root} push={true} />;
        }
        const workerPoolSummary = this.props.workerPoolSummary;
        const machinesHealthLinks = orderedHealthStatuses.map((status) => this.renderMachineSummaryLinks(this.props.workerPoolSummary, status));
        const summaryComponents = [...machinesHealthLinks, this.renderMachineDisabledSummaryLinks(workerPoolSummary)];

        const workerPool = workerPoolSummary.WorkerPool;
        const overflowMenuItems: Array<OverflowMenuNavLink[] | OverflowMenuGenericItem> = [
            [
                ...(workerPoolSummary && this.props.workerPoolSummary.WorkerPool.CanAddWorkers
                    ? [
                          OverflowMenuItems.navItem("Add Worker", routeLinks.infrastructure.workerMachines.new(workerPool.Id), undefined, {
                              permission: Permission.WorkerEdit,
                          }),
                      ]
                    : []),
                OverflowMenuItems.navItem("Edit", routeLinks.infrastructure.workerPool(workerPool), undefined, {
                    permission: Permission.WorkerEdit,
                }),
            ],
        ];

        // Only show machine-related actions if they actually have some machines in this workerPool.
        if (workerPoolSummary.TotalMachines > 0) {
            if (this.state.machinesResponse) {
                const machineIds = this.state.machinesResponse.Items.map((x) => x.Id);
                overflowMenuItems.push(
                    OverflowMenuItems.item(`Check Health for ${workerPoolSummary.TotalMachines} Worker${workerPoolSummary.TotalMachines === 1 ? "" : "s"}`, () => this.performHealthCheck(workerPool, machineIds), {
                        permission: Permission.WorkerEdit,
                    })
                );
            }

            const tentacleIds = workerPoolSummary.MachineIdsForTentacleUpgrade;
            if (tentacleIds && tentacleIds.length > 0) {
                overflowMenuItems.push(
                    OverflowMenuItems.confirmUpgrade(`Upgrade ${tentacleIds.length} Tentacle${tentacleIds.length === 1 ? "" : "s"} in this Worker Pool`, () => this.performTentacleUpgrade(workerPool, tentacleIds), {
                        permission: Permission.WorkerEdit,
                    })
                );
            }
            const calamariIds = workerPoolSummary.MachineIdsForCalamariUpgrade;
            if (calamariIds && calamariIds.length > 0) {
                overflowMenuItems.push(
                    OverflowMenuItems.confirmUpgrade(`Upgrade Calamari on ${calamariIds.length} Deployment Target${calamariIds.length === 1 ? "" : "s"}`, () => this.performCalamariUpgrade(calamariIds), {
                        permission: Permission.WorkerEdit,
                    })
                );
            }
        }

        const workerTypeId = (this.props.workerPoolSummary.WorkerPool as DynamicWorkerPoolResource).WorkerType;
        const workerType = this.props.workerTypes.find((x) => x.Type === workerTypeId);

        const titleContainer = (
            <div className={styles.cardTitleContainer}>
                <div className={styles.workerPoolIcon}>{this.getWorkerPoolIcon(workerPool)}</div>
                <div className={styles.workerPoolName}>{workerPool.Name}</div>
                {this.props.workerPoolSummary && this.props.workerPoolSummary.WorkerPool.CanAddWorkers && <div className={styles.workerPoolMachinesCount}>({workerPoolSummary.TotalMachines && workerPoolSummary.TotalMachines.toLocaleString()})</div>}
                {this.getDynamicWorkerDescription(workerTypeId, workerType)}
                <DeprecatedDynamicWorkerTypeChip workerType={workerType} />
                <div className={styles.workerPoolSummaryCounts}>{summaryComponents}</div>
                <div className={styles.workerPoolOverflowActions}>
                    <OverflowMenu menuItems={overflowMenuItems} />
                </div>
            </div>
        );

        return (
            <PaperLayout key={workerPool.Id} busy={this.state.busy} errors={this.errors} className={styles.paperLayoutOverride}>
                <SimpleExpander
                    errorKey={workerPool.Id}
                    key={workerPool.Id}
                    title={titleContainer}
                    onDidExpand={(expanded) => {
                        this.setState({ expanded });
                        if (expanded) {
                            this.reloadDataAndCurrentPageIndex();
                        } else {
                            this.setState({
                                machinesResponse: null,
                                currentPageIndex: 0,
                            });
                        }
                    }}
                >
                    {workerPool.Description && (
                        <Section>
                            <MarkdownDescription markup={workerPool.Description} />
                        </Section>
                    )}
                    {workerPool.CanAddWorkers && this.renderMachinesList(workerPoolSummary)}
                    {workerPool.WorkerPoolType === WorkerPoolType.Dynamic && this.renderDynamicWorkerCallout()}
                </SimpleExpander>
            </PaperLayout>
        );
    }

    renderDynamicWorkerCallout(): React.ReactNode {
        return (
            <Callout
                title={
                    <span>
                        This is a{" "}
                        <ExternalLink href="DynamicWorkerPools" showIcon={true}>
                            Dynamic Worker Pool
                        </ExternalLink>
                    </span>
                }
                type={CalloutType.Information}
            >
                There are no workers in this worker pool; Octopus will provide workers on-demand during deployments.
            </Callout>
        );
    }

    getWorkerPoolIcon(workerPool: WorkerPoolResource): React.ReactNode {
        switch (workerPool.WorkerPoolType) {
            case WorkerPoolType.Static:
                return withTheme((theme) => <OctopusIcon iconType={OctopusIconType.WorkerPool} color={theme.iconDark} />);
            case WorkerPoolType.Dynamic:
                return withTheme((theme) => <OctopusIcon iconType={OctopusIconType.DynamicWorkerPool} color={theme.iconDark} />);
        }
    }

    protected async loadData() {
        const isDisabled = getIsDisabledFilterAsPerPrecedence(this.props.isFiltering, this.props.filter, this.state.isDisabledFilter);
        const applicableHealthStatusFilters = getHealthStatusFiltersAsPerPrecedence(this.props.filter, this.props.isFiltering, this.state.healthStatusFilter);
        const workerPoolRequestArgs = createWorkerPoolsMachinesArgs(this.props.filter, isDisabled, applicableHealthStatusFilters);
        await this.requestRaceConditioner.avoidStaleResponsesForRequest(repository.WorkerPools.machines(this.props.workerPoolSummary.WorkerPool, workerPoolRequestArgs), (machinesResponse) => this.setMachineResponseState(machinesResponse));
    }

    private getDynamicWorkerDescription(workerTypeId: string, workerType: DynamicWorkerType | undefined) {
        if (this.props.workerPoolSummary && this.props.workerPoolSummary.WorkerPool.WorkerPoolType === WorkerPoolType.Dynamic) {
            if (workerType) {
                return (
                    <div className={styles.workerPoolMachinesCount}>
                        {workerType.Description}
                        {workerType?.Type.endsWith("Default") ? " (default)" : ""}
                    </div>
                );
            }
            return (
                <div className={styles.workerPoolMachinesCount}>
                    <MissingChip lookupId={workerTypeId}></MissingChip>
                </div>
            );
        }
        return null;
    }

    private renderMachineSummaryLinks(workerPoolSummary: WorkerPoolSummaryResource, healthStatus: MachineModelHealthStatus) {
        const healthStatusIcon = this.machineIconHelper.healthStatusIcons[healthStatus];
        const value = workerPoolSummary.MachineHealthStatusSummaries[healthStatus];
        if (!value || value === 0) {
            // Don't show links if there's nothing to report.
            return null;
        }

        // If filtering health statuses from the sidebar, just show the health statuses that they've chosen to filter (and don't show them as links).
        if (this.props.filter.healthStatuses && this.props.filter.healthStatuses.length > 0) {
            return (
                <div key={healthStatus} className={styles.summaryCount}>
                    {healthStatusIcon && <img key={healthStatus} src={healthStatusIcon} className={styles.healthStatusIcon} />}
                    {value.toLocaleString() + " " + MachineHealthStatusHelper.getFriendlyName(healthStatus).toLowerCase()}
                </div>
            );
        }

        // Else show workerPool-specific health status actions.
        if (this.state.healthStatusFilter === healthStatus) {
            return (
                <div key={healthStatus} className={styles.summaryCount}>
                    {healthStatusIcon && <img key={healthStatus} src={healthStatusIcon} className={styles.healthStatusIcon} />}
                    {value.toLocaleString() + " " + MachineHealthStatusHelper.getFriendlyName(healthStatus).toLowerCase()}
                    <SmallCloseButton
                        onClose={(e: React.MouseEvent) => {
                            // The user may click a health status link to open an expander (but it shouldn't ever close it).
                            if (this.state.expanded) {
                                e.preventDefault();
                                e.stopPropagation(); //prevent clicking the link toggling the panel/expander.
                            }

                            this.setState({ healthStatusFilter: "" }, () => {
                                if (this.state.expanded) {
                                    this.reloadDataAndCurrentPageIndex();
                                }
                            });
                        }}
                    />
                </div>
            );
        } else {
            return (
                <ActionButton
                    key={healthStatus}
                    icon={<img key={healthStatus} src={healthStatusIcon} className={styles.healthStatusIcon} />}
                    className={styles.summaryCount}
                    type={ActionButtonType.Ternary}
                    label={value.toLocaleString() + " " + MachineHealthStatusHelper.getFriendlyName(healthStatus).toLowerCase()}
                    onClick={(e: React.MouseEvent) => {
                        // The user may click a health status link to open an expander (but it shouldn't ever close it).
                        if (this.state.expanded) {
                            e.preventDefault();
                            e.stopPropagation(); //prevent clicking the link toggling the panel/expander.
                        }
                        // Clear any disabled filters when a healthStatus filter is clicked. You can't chain inline disabled and healthStatus
                        // filters together because they use different and/or logic at the API and it causes UI confusion.
                        this.setState(
                            {
                                healthStatusFilter: healthStatus,
                                isDisabledFilter: false,
                            },
                            () => {
                                if (this.state.expanded) {
                                    this.reloadDataAndCurrentPageIndex();
                                }
                            }
                        );
                    }}
                />
            );
        }
    }

    private renderMachineDisabledSummaryLinks(workerPoolSummary: WorkerPoolSummaryResource) {
        const disabledComponentKey = "Disabled";
        const disabledIcon = this.machineIconHelper.healthStatusIcons["Disabled"];
        const value = workerPoolSummary.TotalDisabledMachines;
        if (!value || value === 0) {
            // Don't show links if there's nothing to report.
            return null;
        }

        // If filtering from the sidebar, just show the disabled control (not as a link).
        if (this.props.filter.isDisabled) {
            return (
                <div key={disabledComponentKey} className={styles.summaryCount}>
                    {disabledIcon && <img key={disabledComponentKey} src={disabledIcon} className={styles.healthStatusIcon} />}
                    {value.toLocaleString() + " disabled"}
                </div>
            );
        }

        // Else show workerPool-specific disabled action.
        if (this.state.isDisabledFilter) {
            return (
                <div key={disabledComponentKey} className={styles.summaryCount}>
                    {disabledIcon && <img key={disabledComponentKey} src={disabledIcon} className={styles.healthStatusIcon} />}
                    {value.toLocaleString() + " disabled"}
                    <SmallCloseButton
                        onClose={(e: React.MouseEvent) => {
                            // The user may click a health status link to open an expander (but it shouldn't ever close it).
                            if (this.state.expanded) {
                                e.preventDefault();
                                e.stopPropagation(); //prevent clicking the link toggling the panel/expander.
                            }

                            this.setState({ isDisabledFilter: false }, () => {
                                if (this.state.expanded) {
                                    this.reloadDataAndCurrentPageIndex();
                                }
                            });
                        }}
                    />
                </div>
            );
        } else {
            return (
                <ActionButton
                    key={disabledComponentKey}
                    icon={<img key={disabledComponentKey} src={disabledIcon} className={styles.healthStatusIcon} />}
                    className={styles.summaryCount}
                    type={ActionButtonType.Ternary}
                    label={value.toLocaleString() + " disabled"}
                    onClick={(e: React.MouseEvent) => {
                        // The user may click a disabled link to open an expander (but it shouldn't ever close it).
                        if (this.state.expanded) {
                            e.preventDefault();
                            e.stopPropagation(); //prevent clicking the link toggling the panel/expander.
                        }
                        // Clear any healthStatus filters when disabled is clicked. You can't chain inline disabled and healthStatus
                        // filters together because they use different and/or logic at the API and it causes UI confusion.
                        this.setState(
                            {
                                isDisabledFilter: true,
                                healthStatusFilter: "",
                            },
                            () => {
                                if (this.state.expanded) {
                                    this.reloadDataAndCurrentPageIndex();
                                }
                            }
                        );
                    }}
                />
            );
        }
    }

    private async performHealthCheck(workerPool: WorkerPoolResource, machineIds: string[]) {
        return this.doBusyTask(async () => {
            const task = await repository.Tasks.createPerformHealthCheckTaskForWorkerPool(workerPool, machineIds);
            this.setState({ redirectToTaskId: task.Id });
        });
    }

    private async performTentacleUpgrade(workerPool: WorkerPoolResource, machineIds: string[]) {
        return this.doBusyTask(async () => {
            const task = await repository.Tasks.createUpgradeTentacleOnWorkerPoolTask(workerPool, machineIds);
            this.setState({ redirectToTaskId: task.Id });
        });
    }

    private async performCalamariUpgrade(machineIds: string[]) {
        return this.doBusyTask(async () => {
            const task = await repository.Tasks.createUpdateCalamariOnWorkersTask(machineIds);
            this.setState({ redirectToTaskId: task.Id });
        });
    }
}

export default WorkerPoolSummarySection;
